// Moved out of skill-map-view.js to keep the view to one concern

/**
 * The trade rows the skill routing view lists: one per trade, with its de-duplicated skills,
 * then filtered, searched and sorted.
 *
 * @returns {object[]}
 */
export function buildSkillMapTradeRows({ map, lines, search, sortMode, conditionalRoutingOnly, conditionalTradesOn }) {
    if (!map || !lines) return []
    const trades = map.trades || {}
    const grouped = new Map()
    const skills = buildUniqueSkillRows(lines)
    for (const skill of skills) {
        const { tradeId } = skill
        const row = getOrCreateTradeRow(grouped, tradeId, trades)
        row.skills.push(skill)
        row.hasConditionalRouting ||= conditionalTradesOn && hasConditionalTrades(skill)
    }
    // FIQ-2457: every trade is listed, with or without skills and whatever the flag, so a
    // trade made while schedule assignments were on stays visible and removable when off
    for (const tradeId of Object.keys(trades)) getOrCreateTradeRow(grouped, tradeId, trades)
    for (const row of grouped.values()) {
        // Skills routed here by conditional rules, for the row's label
        row.conditionalRuleCount = conditionalTradesOn ? countSkillsRoutingToTrade(skills, row.tradeId) : 0
        // Skills whose rules go when the trade is removed, for the removal warning. Counted
        // whatever the conditional trades flag, since the server removes stored rules either way
        row.rulesRemovedSkillCount = countSkillsLosingRulesWithTrade(skills, row.tradeId)
    }
    let rows = [...grouped.values()]
    if (conditionalRoutingOnly && conditionalTradesOn) {
        rows = rows.filter((row) => row.hasConditionalRouting)
    }
    if (search) {
        const q = search.toLowerCase()
        rows = rows.filter(
            (r) =>
                r.tradeName.toLowerCase().includes(q) ||
                r.skills.some((s) => s.skillName.toLowerCase().includes(q) || s.skillCode.toLowerCase().includes(q))
        )
    }
    if (sortMode === "alpha") {
        rows.sort((a, b) => a.tradeName.localeCompare(b.tradeName))
    } else if (sortMode === "skills-desc") {
        rows.sort((a, b) => b.skills.length - a.skills.length)
    } else if (sortMode === "skills-asc") {
        rows.sort((a, b) => a.skills.length - b.skills.length)
    }
    return rows
}

export function buildUniqueSkillRows(lines) {
    const skillsById = new Map()
    for (const line of lines) {
        if (!line.tradeId) continue
        const skill = buildSkillRow(line)
        if (!skillsById.has(skill.skillId)) {
            skillsById.set(skill.skillId, skill)
            continue
        }
        skillsById.set(skill.skillId, mergeSkillRows(skillsById.get(skill.skillId), skill))
    }
    return [...skillsById.values()]
}

/**
 * How many of these (de-duplicated) skills have a conditional rule sending work to the trade.
 *
 * @param {object[]} skills From buildUniqueSkillRows
 * @param {string} tradeId
 * @returns {number}
 */
export function countSkillsRoutingToTrade(skills, tradeId) {
    if (!tradeId) return 0
    return skills.filter(routesToTrade).length

    function routesToTrade(skill) {
        return (skill.conditionalTrades || []).some(isForTrade)
    }

    function isForTrade(conditional) {
        return conditional?.tradeId === tradeId
    }
}

/**
 * How many of these (de-duplicated) skills lose conditional rules when the trade is removed:
 * those with a rule sending work to it, and its own skills with any rules at all, because a
 * skill whose default trade goes loses all its rules (see pruneRemovedTradeReferences).
 *
 * @param {object[]} skills From buildUniqueSkillRows
 * @param {string} tradeId
 * @returns {number}
 */
export function countSkillsLosingRulesWithTrade(skills, tradeId) {
    if (!tradeId) return 0
    return skills.filter(losesRules).length

    function losesRules(skill) {
        const rules = skill.conditionalTrades || []
        if (skill.tradeId === tradeId) return rules.length > 0
        return rules.some(isForTrade)
    }

    function isForTrade(conditional) {
        return conditional?.tradeId === tradeId
    }
}

function getOrCreateTradeRow(grouped, tradeId, trades) {
    if (!grouped.has(tradeId)) {
        grouped.set(tradeId, {
            tradeId,
            tradeName: trades[tradeId] || tradeId,
            skills: [],
            hasConditionalRouting: false,
        })
    }
    return grouped.get(tradeId)
}

function buildSkillRow(line) {
    return {
        id: line.id,
        lineIds: getLineIds(line),
        skillId: line.skillId,
        skillName: line.skillName || line.skillId,
        skillCode: line.skillCode || "",
        isCustomSkill: !!line.isCustomSkill,
        customHourlyRate: line.customHourlyRate,
        taskCount: line.taskCount || 0,
        // Carry routing state through for the edit-mapping drawer.
        tradeId: line.tradeId || null,
        conditionalTrades: line.conditionalTrades || [],
    }
}

function getLineIds(line) {
    if (!line.id) return []
    return [line.id]
}

function mergeSkillRows(existing, incoming) {
    const preferred = getPreferredSkillRow(existing, incoming)
    const fallback = preferred === existing ? incoming : existing
    return {
        ...preferred,
        lineIds: mergeLineIds(existing, incoming),
        conditionalTrades: getPreferredConditionals(preferred, fallback),
    }
}

function getPreferredSkillRow(existing, incoming) {
    if (hasConditionalTrades(incoming) && !hasConditionalTrades(existing)) return incoming
    return existing
}

function getPreferredConditionals(preferred, fallback) {
    if (hasConditionalTrades(preferred)) return preferred.conditionalTrades
    return fallback.conditionalTrades || []
}

function mergeLineIds(existing, incoming) {
    return [...new Set([...(existing.lineIds || []), ...(incoming.lineIds || [])])]
}

function hasConditionalTrades(skill) {
    return Array.isArray(skill.conditionalTrades) && skill.conditionalTrades.length > 0
}
